/**
 * Report client errors to backend (fire-and-forget). Shown in Admin → Analytics → Errors.
 * Source: store (Customer).
 */

import { API_BASE_URL } from '../Config/Api';

const SOURCE = 'store';
const MAX_STRING = { message: 2000, stack: 5000, url: 500 };

// Deduplication: suppress identical errors that fire in bursts (e.g. simultaneous slot fetches
// all failing when the network drops). Each unique message key is allowed once per 30 seconds.
const _recentErrors = new Map(); // key → timestamp
const DEDUP_WINDOW_MS = 30_000;
const DEDUP_MAX_KEYS = 50; // prevent unbounded growth

function isDuplicate(message, requestUrl) {
  const key = `${message}||${requestUrl || ''}`;
  const now = Date.now();
  // Purge stale entries
  if (_recentErrors.size >= DEDUP_MAX_KEYS) {
    for (const [k, t] of _recentErrors) {
      if (now - t > DEDUP_WINDOW_MS) _recentErrors.delete(k);
    }
  }
  if (_recentErrors.has(key) && now - _recentErrors.get(key) < DEDUP_WINDOW_MS) {
    return true;
  }
  _recentErrors.set(key, now);
  return false;
}

function truncate(str, max) {
  if (typeof str !== 'string') return str;
  return str.length <= max ? str : str.slice(0, max) + '…';
}

/**
 * Drop errors we know are not actionable so the production log stays
 * useful. Returns true when the error should be silently ignored.
 *
 * Categories we filter:
 *  - Errors originating in browser extensions (`chrome-extension://`,
 *    `moz-extension://`, `safari-extension://`) — caused by user-
 *    installed extensions mutating the DOM, not by our code.
 *  - Facebook in-app browser instrumentation (`iabjs://…` schemes +
 *    "Java object is gone" postMessage races on Android FB IAB).
 *  - Cross-origin "Script error." with no stack info — useless noise.
 *  - 401s on authenticated /me/* endpoints — those are expected when
 *    a stored token expired; the calling page handles it gracefully
 *    (re-routes to login). Reporting them clutters the dashboard.
 */
export function isNoise(error, context) {
  const stack = String(error?.stack || '');
  const message = String(error?.message || error?.toString?.() || '');
  const requestUrl = String(context?.requestUrl || '');

  // 1. Browser extension stacks — not our code. Frames are often wrapped in
  //    parens, e.g. `at fn (chrome-extension://…)`, so match anywhere.
  if (/(?:chrome|moz|safari|edge|opera)-extension:\/\//i.test(stack)) return true;

  // 2. Facebook / Instagram in-app-browser native-bridge instrumentation. The
  //    IG browser injects sendDataToNative/sendPageHideMessage that call
  //    window.webkit.messageHandlers and throw when it isn't present — their
  //    code, not ours, firing on pagehide of every IG-referred visit.
  if (/iabjs:\/\//.test(stack)) return true;
  if (/\bsendDataToNative\b|\bsendPageHideMessage\b/.test(stack)) return true;
  if (message === 'Error invoking postMessage: Java object is gone') return true;
  if (/window\.webkit\.messageHandlers|window\.safari\.pushNotification/.test(message)) return true;

  // 3. Cross-origin scripts collapse the error info to bare "Script error.".
  if (message === 'Script error.' && !stack) return true;

  // 4. Expected 401s on token-only endpoints — caller handles re-auth.
  if (context?.statusCode === 401 && /\/me\//.test(requestUrl)) return true;

  return false;
}

function getVisitorKey() {
  try {
    let key = localStorage.getItem('anonymousId') || localStorage.getItem('stylon_store_visitor');
    if (!key) {
      key = 'st_' + Math.random().toString(36).slice(2) + '_' + Date.now();
      localStorage.setItem('stylon_store_visitor', key);
    }
    return key;
  } catch (_) {
    return null;
  }
}

export function reportClientError(error, context = {}) {
  const message = error?.message || error?.toString?.() || String(error);
  if (isNoise(error, context)) return;
  if (isDuplicate(message, context.requestUrl)) return;
  const payload = {
    type: context.type || 'error',
    message: truncate(message, MAX_STRING.message),
    url: typeof window !== 'undefined' && window.location ? window.location.href : undefined,
    stack: error?.stack ? truncate(error.stack, MAX_STRING.stack) : undefined,
    componentStack: context.componentStack ? truncate(context.componentStack, MAX_STRING.componentStack) : undefined,
  };
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    payload.userAgent = truncate(navigator.userAgent, 400);
  }
  if (context.statusCode != null) payload.statusCode = context.statusCode;
  if (context.requestUrl != null) payload.requestUrl = truncate(String(context.requestUrl), 512);
  const body = {
    source: SOURCE,
    eventType: 'client_error',
    visitorKey: getVisitorKey() || undefined,
    payload,
  };
  try {
    const base = typeof API_BASE_URL === 'string' && API_BASE_URL ? API_BASE_URL.replace(/\/+$/, '') : '/v1';
    fetch(`${base}/analytics/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
}
